import { useState, useMemo, useEffect } from "react";
import {
  Search,
  Plus,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle2,
  Trash2,
  Send,
  History,
  LayoutTemplate,
} from "lucide-react";
import StatusHistoryModal from "../../components/StatusHistoryModal";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Tabs,
  message,
  Popconfirm,
  Spin,
  Empty,
  Alert,
  Drawer,
  List,
} from "antd";
import dayjs from "dayjs";
import {
  IQuote,
  IQuoteCreatePayload,
  IQuoteLineItem,
} from "../../../types/api/quote";
import { IClient } from "../../../types/api/client";
import {
  useQuoteList,
  useQuoteCreate,
  useQuoteUpdate,
  useQuoteDelete,
  useQuoteSend,
  useQuoteConvertToJob,
  useQuoteAcceptOnBehalf,
  useQuoteStats,
} from "../../../hooks/api/quotes/hooks";
import {
  useQuoteTemplates,
  useCreateQuoteTemplate,
  useDeleteQuoteTemplate,
} from "../../../hooks/api/quotes/quote-template-hooks";
import { IQuoteTemplate } from "../../../types/api/quoteTemplate";
import { useClientList } from "../../../hooks/api/clients/hooks";
import QuotesTable from "./QuotesTable";
import { useMessageApi } from "../../../../src/provider/AntdNotification";
import { useQueryState } from "nuqs";
import { useTerm } from "../../../lib/terminology";
import { QUOTE_STATUS_CFG as STATUS_CFG } from "../../../constants/statusColors";
import Card from "../../../components/ui/Card";

const TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "viewed", label: "Viewed" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
  { key: "converted", label: "Converted" },
];

const VAT_OPTS = [
  { value: 20, label: "20% Standard" },
  { value: 5, label: "5% Reduced" },
  { value: 0, label: "0% Zero" },
];

const CLIENT_Q = { page: 1, limit: 200 };

function fmt(n: number | string | undefined | null) {
  const v = Number(n ?? 0);
  return `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const SummaryCard = ({
  icon,
  iconBg,
  value,
  label,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  value: string | number;
  label: string;
  sub?: string;
}) => (
  <Card className="p-4 flex items-center gap-4">
    <div
      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
    >
      {icon}
    </div>
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-800 leading-tight">
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </Card>
);

// Editable line items table inside the form
const ITEM_TYPE_OPTS = [
  { value: "labour", label: "Labour" },
  { value: "materials", label: "Materials" },
  { value: "callout", label: "Callout Fee" },
  { value: "travel", label: "Travel" },
  { value: "other", label: "Other" },
];

const LineItemsEditor = ({
  value = [],
  onChange,
}: {
  value?: IQuoteLineItem[];
  onChange?: (items: IQuoteLineItem[]) => void;
}) => {
  function update(idx: number, field: string, val: any) {
    const next = value.map((item: any, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: val };
      updated.line_total =
        Number(updated.quantity ?? 0) * Number(updated.unit_price ?? 0);
      return updated;
    });
    onChange?.(next as IQuoteLineItem[]);
  }

  function addRow() {
    onChange?.([
      ...value,
      {
        description: "",
        quantity: 1,
        unit_price: 0,
        vat_rate: 20,
        line_total: 0,
        item_type: "other",
      } as any,
    ]);
  }

  function removeRow(idx: number) {
    onChange?.(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
              <th className="text-left px-3 py-2 w-[30%]">Description</th>
              <th className="text-left px-2 py-2 w-[18%]">Type</th>
              <th className="text-right px-3 py-2 w-14">Qty</th>
              <th className="text-right px-3 py-2 w-24">Unit Price</th>
              <th className="text-right px-3 py-2 w-16">VAT %</th>
              <th className="text-right px-3 py-2 w-24">Total</th>
              <th className="px-2 py-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {value.map((item: any, idx) => (
              <tr key={idx} className="border-t border-slate-100">
                <td className="px-2 py-1.5">
                  <Input
                    size="small"
                    value={item.description}
                    onChange={(e) => update(idx, "description", e.target.value)}
                    placeholder="Line item description"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    size="small"
                    className="w-full"
                    value={item.item_type || "other"}
                    options={ITEM_TYPE_OPTS}
                    onChange={(v) => update(idx, "item_type", v)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <InputNumber
                    size="small"
                    className="w-full"
                    min={0}
                    value={item.quantity}
                    onChange={(v) => update(idx, "quantity", v ?? 0)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <InputNumber
                    size="small"
                    className="w-full"
                    min={0}
                    prefix="£"
                    value={item.unit_price}
                    onChange={(v) => update(idx, "unit_price", v ?? 0)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    size="small"
                    className="w-full"
                    value={item.vat_rate}
                    options={VAT_OPTS}
                    onChange={(v) => update(idx, "vat_rate", v)}
                  />
                </td>
                <td className="px-3 py-1.5 text-right text-slate-700 font-medium tabular-nums">
                  {fmt(item.line_total)}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-red-400 hover:text-red-600"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {value.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-4 text-slate-400 text-xs"
                >
                  No line items yet. Click "Add Line" to begin.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="mt-2 text-primary text-sm hover:underline flex items-center gap-1"
      >
        <Plus size={14} /> Add Line
      </button>
    </div>
  );
};

// Send quote modal
const SendModal = ({
  quote,
  onClose,
  clients,
}: {
  quote: IQuote | null;
  onClose: () => void;
  clients: IClient[];
}) => {
  const messageApi = useMessageApi();

  const clientEmail = useMemo(() => {
    if (!quote?.client_id) return "";
    const c = clients.find((x) => x.id === quote.client_id);
    return c?.email ?? "";
  }, [quote, clients]);

  const [email, setEmail] = useState(clientEmail);

  useEffect(() => {
    setEmail(clientEmail);
  }, [clientEmail]);

  const { mutate: sendQuote, isPending } = useQuoteSend({
    onSuccess: () => {
      messageApi.success("Quote sent to client.");
      onClose();
    },
    onError: (err: any) => {
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to send quote.");
    }
  });

  if (!quote) return null;

  return (
    <Modal
      open={!!quote}
      onCancel={onClose}
      title={
        <span className="text-base font-semibold">
          Send Quote — {quote.quote_number}
        </span>
      }
      width="min(420px, 95vw)"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={isPending}
          disabled={!email}
          onClick={() => sendQuote({ id: quote.id, client_email: email })}
        >
          Send
        </Button>,
      ]}
    >
      <div className="py-2">
        <label className="block text-sm text-slate-600 mb-1">
          Client Email
        </label>
        <Input
          type="email"
          placeholder="client@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="text-xs text-slate-400 mt-2">
          A PDF will be generated and saved, and a link will be provided.
        </p>
        {(quote as any).public_token && (
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <label className="block text-xs text-slate-500 mb-1">
              Public URL
            </label>
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/quote/${(quote as any).public_token}`}
                readOnly
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/quote/${(quote as any).public_token}`,
                  );
                  message.success("Link copied!");
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const QuotesPage = () => {
  const term = useTerm();
  const [search, setSearch] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabFilter, setTabFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<IQuote | null>(null);
  const [sendingQuote, setSendingQuote] = useState<IQuote | null>(null);
  const [historyQuote, setHistoryQuote] = useState<IQuote | null>(null);
  const [form] = Form.useForm();
  const [quoteId, setQuoteId] = useQueryState("quote");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateNameForm] = Form.useForm();

  

const queryData = useMemo(() => {
  const q: any = { page, limit };

  if (tabFilter !== "all") {
    q.status = tabFilter;
  }

  if (debouncedSearch.trim()) {
    q.search = debouncedSearch.trim();
  }

  return q;
}, [page, limit, tabFilter, debouncedSearch]);

  const {
    data: listRes,
    isLoading,
    isError,
    error,
    refetchWithQuery: refetch,
  } = useQuoteList(queryData);

  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500);

  return () => clearTimeout(timer);
}, [search]);
  // Refetch with updated queryData when tab or search changes
  useEffect(() => {    
    refetch(queryData);
  }, [queryData]);
  const { data: clientRes } = useClientList(CLIENT_Q);
  const { data: statsRes } = useQuoteStats();

  const quotes: IQuote[] = (listRes?.data as IQuote[] | undefined) ?? [];
  const totalQuotes = (listRes as any)?.extra?.total ?? quotes.length;
  const clients: IClient[] = (clientRes?.data as IClient[] | undefined) ?? [];

  const globalStats = (statsRes as any)?.data as Record<string, number> | undefined;

  const clientOptions = clients.map((c) => {
    const name = c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";
    const sub = c.email || c.phone || c.mobile || "";
    return { value: c.id, label: sub ? `${name} (${sub})` : name };
  });

  const { mutate: createQuote, isPending: creating } = useQuoteCreate({
    qData: queryData,
    onSuccess: () => {
      message.success("Quote created.");
      closeModal();
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? "Failed to create quote."),
  });

  const { mutate: updateQuote, isPending: updating } = useQuoteUpdate({
    qData: queryData,
    onSuccess: () => {
      message.success("Quote updated.");
      closeModal();
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? "Failed to update quote."),
  });

  const { mutate: convertToJob, isPending: converting } = useQuoteConvertToJob({
    onSuccess: (res) => {
      message.success(`Quote converted — Job ${res.data?.job_number ?? ""} created.`);
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to convert quote."),
  });

  const { mutate: acceptOnBehalf, isPending: accepting } = useQuoteAcceptOnBehalf({
    onSuccess: () => {
      message.success("Quote accepted on behalf of client.");
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to accept quote."),
  });

  const { mutate: deleteQuote } = useQuoteDelete({
    qData: queryData,
    onSuccess: () => {
      message.success("Quote deleted.");
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? "Failed to delete quote."),
  });

  const { data: templatesRes, isLoading: templatesLoading } = useQuoteTemplates();
  const templates: IQuoteTemplate[] = (templatesRes?.data as IQuoteTemplate[] | undefined) ?? [];

  const { mutate: createTemplate, isPending: savingTemplate } = useCreateQuoteTemplate({
    onSuccess: () => {
      message.success("Template saved.");
      setSaveTemplateOpen(false);
      templateNameForm.resetFields();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e?.response?.data?.message ?? e?.message ?? "Failed to save template.");
    },
  });

  const { mutate: deleteTemplate } = useDeleteQuoteTemplate({
    onSuccess: () => message.success("Template deleted."),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e?.response?.data?.message ?? e?.message ?? "Failed to delete template.");
    },
  });

  // Summary calculations — monetary totals from current page, counts from global stats API
  const totalValue = quotes.reduce((sum, q) => sum + Number(q.total ?? 0), 0);
  const accepted = quotes.filter(
    (q) => q.status === "accepted" || q.status === "converted",
  );
  const acceptedVal = accepted.reduce(
    (sum, q) => sum + Number(q.total ?? 0),
    0,
  );

  // Global counts from /quotes/stats endpoint
  const globalAll = globalStats ? (globalStats.all ?? 0) : null;
  const globalAccepted = globalStats ? ((globalStats.accepted ?? 0) + (globalStats.converted ?? 0)) : null;
  const globalPending = globalStats ? ((globalStats.sent ?? 0) + (globalStats.viewed ?? 0)) : null;
  const globalConvRate = globalAll && globalAll > 0 && globalAccepted !== null
    ? Math.round((globalAccepted / globalAll) * 100)
    : null;

  // Fallback to page-level if stats not yet loaded
  const pending = quotes.filter(
    (q) => q.status === "sent" || q.status === "viewed",
  );
  const convRate = quotes.length
    ? Math.round((accepted.length / quotes.length) * 100)
    : 0;

  const tabItems = TABS.map((t) => ({
    key: t.key,
    label: <span className="flex items-center gap-1.5 text-sm">{t.label}</span>,
  }));

  function handleTabChange(key: string) {
    setTabFilter(key);
    setPage(1);
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    setPage(1);
  }

  function openCreate() {
    setEditingQuote(null);
    form.resetFields();
    form.setFieldValue("line_items", []);
    form.setFieldValue("vat_rate", 20);
    setModalOpen(true);
  }

  function openEdit(quote: IQuote) {
    setEditingQuote(quote);
    form.setFieldsValue({
      ...quote,
      issue_date: quote.issue_date ? dayjs(quote.issue_date) : null,
      expiry_date: quote.expiry_date ? dayjs(quote.expiry_date) : null,
      line_items: quote.line_items ?? [],
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingQuote(null);
    setQuoteId(null);
    form.resetFields();
  }

  function onFinish(values: any) {
    const payload = {
      ...values,
      issue_date: values.issue_date
        ? dayjs(values.issue_date).format("YYYY-MM-DD")
        : undefined,
      expiry_date: values.expiry_date
        ? dayjs(values.expiry_date).format("YYYY-MM-DD")
        : undefined,
    };
    if (editingQuote) {
      updateQuote({ id: editingQuote.id, ...payload });
    } else {
      createQuote(payload as IQuoteCreatePayload);
    }
  }

  useEffect(() => {
  if (quoteId) {
    const quote = quotes.find((q) => String(q.id) === quoteId);

    if (quote) {
      setEditingQuote(quote);
      form.setFieldsValue({
        ...quote,
        issue_date: quote.issue_date ? dayjs(quote.issue_date) : null,
        expiry_date: quote.expiry_date ? dayjs(quote.expiry_date) : null,
        line_items: quote.line_items ?? [],
      });

      setModalOpen(true);
    }
  } else {
    setModalOpen(false);
    setEditingQuote(null);
  }
}, [quoteId, quotes]);

  return (
    <div className="min-h-screen bg-slate-50/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{term("quote", true)}</h1>
          <p className="text-slate-400 text-sm">
            Create, track and convert quotes to jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            icon={<LayoutTemplate size={15} />}
            onClick={() => setTemplatesOpen(true)}
            className="flex items-center gap-1.5"
          >
            Templates
          </Button>
          <ButtonWrapper
            type="primary"
            icon={<Plus size={15} />}
            onClick={openCreate}
            className="flex items-center gap-1.5"
          >
            New {term("quote")}
          </ButtonWrapper>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          icon={<FileText size={18} className="text-blue-500" />}
          iconBg="bg-blue-50"
          value={fmt(totalValue)}
          label="Total Pipeline"
          sub={globalAll !== null ? `${globalAll} quotes total` : `${quotes.length} quotes`}
        />
        <SummaryCard
          icon={<CheckCircle2 size={18} className="text-emerald-500" />}
          iconBg="bg-emerald-50"
          value={fmt(acceptedVal)}
          label="Accepted Value"
          sub={globalAccepted !== null ? `${globalAccepted} accepted` : `${accepted.length} quotes`}
        />
        <SummaryCard
          icon={<Clock size={18} className="text-amber-500" />}
          iconBg="bg-amber-50"
          value={globalPending !== null ? globalPending : pending.length}
          label="Awaiting Response"
          sub="sent or viewed"
        />
        <SummaryCard
          icon={<TrendingUp size={18} className="text-indigo-500" />}
          iconBg="bg-indigo-50"
          value={`${globalConvRate !== null ? globalConvRate : convRate}%`}
          label="Conversion Rate"
          sub="global · accepted / total"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs
            activeKey={tabFilter}
            onChange={handleTabChange}
            items={tabItems}
            className="flex-1"
            style={{ marginBottom: 0 }}
          />
          <div className="relative w-56">
            <Search
              size={14}
              className="text-slate-400 absolute left-3 top-2.5 z-10"
            />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search quotes…"
              className="pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      {isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load data"
            description={(error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : quotes.length === 0 ? (
          <Empty description="No quotes found" className="py-20" />
        ) : (
          <QuotesTable
            data={quotes}
            loading={isLoading}
            qData={queryData}
            refetch={refetch}
            total={totalQuotes}
            onEdit={openEdit}
            onSend={setSendingQuote}
            onDelete={(id) => deleteQuote({ ids: id })}
            onHistory={setHistoryQuote}
            onConvertToJob={(q) => convertToJob({ id: q.id })}
            onAcceptOnBehalf={(q) => acceptOnBehalf({ id: q.id })}
          />
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <span className="text-base font-semibold">
            {editingQuote ? `Edit — ${editingQuote.quote_number}` : `New ${term("quote")}`}
          </span>
        }
        width="min(720px, 95vw)"
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="draft"
            loading={creating || updating}
            onClick={() => {
              form.setFieldValue("status", "draft");
              form.submit();
            }}
          >
            Save as Draft
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={creating || updating}
            onClick={() => form.submit()}
          >
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          size="middle"
          className="pt-2"
          onFinish={onFinish}
        >
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="client_id" label="Client" rules={[{ required: true, message: "Client is required" }]}>
              <Select
                showSearch
                placeholder="Select client"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={clientOptions}
              />
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="draft">
              <Select
                options={Object.entries(STATUS_CFG).map(([v, c]) => ({
                  value: v,
                  label: c.label,
                }))}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. Annual HVAC Maintenance Contract" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional description…" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="issue_date" label="Issue Date">
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item
              name="expiry_date"
              label="Expiry Date"
              dependencies={["issue_date"]}
              extra="Client cannot accept after this date"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const issue = getFieldValue("issue_date");
                    if (!value || !issue || value.isAfter(issue)) return Promise.resolve();
                    return Promise.reject(new Error("Expiry date must be after issue date"));
                  },
                }),
              ]}
            >
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </div>

          <Form.Item label="Line Items" name="line_items">
            <LineItemsEditor />
          </Form.Item>
          <div className="flex justify-end -mt-2 mb-2">
            <Button
              type="text"
              size="small"
              className="text-slate-500 hover:text-primary"
              onClick={() => setSaveTemplateOpen(true)}
            >
              Save as Template
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-x-4">
            <Form.Item
              name="vat_rate"
              label="Default VAT Rate"
              initialValue={20}
            >
              <Select options={VAT_OPTS} />
            </Form.Item>
            <Form.Item name="discount_amount" label="Discount (£)">
              <InputNumber
                className="w-full"
                min={0}
                prefix="£"
                placeholder="0.00"
              />
            </Form.Item>
          </div>

          <Form.Item name="notes" label="Notes to Client">
            <Input.TextArea
              rows={2}
              placeholder="Optional notes visible to client…"
            />
          </Form.Item>
          <Form.Item name="terms" label="Terms & Conditions">
            <Input.TextArea rows={2} placeholder="Payment terms, conditions…" />
          </Form.Item>
        </Form>
      </Modal>

      <SendModal quote={sendingQuote} onClose={() => setSendingQuote(null)} clients={clients} />

      {historyQuote && (
        <StatusHistoryModal
          open={!!historyQuote}
          entityType="quote"
          entityId={historyQuote.id}
          entityLabel={historyQuote.quote_number}
          onClose={() => setHistoryQuote(null)}
        />
      )}

      <Drawer
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title="Quote Templates"
        width={500}
        extra={
          <Button
            onClick={() => setSaveTemplateOpen(true)}
            size="small"
          >
            Save Current as Template
          </Button>
        }
      >
        {templatesLoading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : templates.length === 0 ? (
          <Empty description="No templates saved yet" className="mt-10" />
        ) : (
          <List
            dataSource={templates}
            renderItem={(tpl) => (
              <List.Item
                key={tpl.id}
                actions={[
                  <Button
                    key="use"
                    type="primary"
                    size="small"
                    onClick={() => {
                      setTemplatesOpen(false);
                      setEditingQuote(null);
                      form.resetFields();
                      form.setFieldValue("line_items", tpl.line_items ?? []);
                      form.setFieldValue("vat_rate", 20);
                      setModalOpen(true);
                    }}
                  >
                    Use
                  </Button>,
                  <Popconfirm
                    key="delete"
                    title="Delete this template?"
                    onConfirm={() => deleteTemplate(tpl.id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger size="small">
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={<span className="font-medium text-slate-800">{tpl.name}</span>}
                  description={
                    <span className="text-xs text-slate-400">
                      {(tpl.line_items ?? []).length} item{(tpl.line_items ?? []).length !== 1 ? "s" : ""}
                      {tpl.created_at
                        ? ` · ${new Date(tpl.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                        : ""}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Modal
        open={saveTemplateOpen}
        onCancel={() => {
          setSaveTemplateOpen(false);
          templateNameForm.resetFields();
        }}
        title={<span className="text-base font-semibold">Save as Template</span>}
        width="min(400px, 95vw)"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSaveTemplateOpen(false);
              templateNameForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={savingTemplate}
            onClick={() => templateNameForm.submit()}
          >
            Save Template
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={templateNameForm}
          layout="vertical"
          size="middle"
          className="pt-2"
          onFinish={(vals) => {
            const lineItems = form.getFieldValue("line_items") ?? [];
            createTemplate({ name: vals.template_name, line_items: lineItems });
          }}
        >
          <Form.Item
            name="template_name"
            label="Template Name"
            rules={[{ required: true, message: "Template name is required" }]}
          >
            <Input placeholder="e.g. Standard HVAC Service" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuotesPage;
